import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, Download, AlertCircle, ArrowRight } from 'lucide-react';
import { shareAPI } from '../api';
import SEO, { SEO_CONFIGS } from '../components/SEO';

const SharedVTO = () => {
  const { shareToken } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSharedVTO();
  }, [shareToken]);

  const fetchSharedVTO = async () => {
    try {
      const response = await shareAPI.getShared(shareToken);
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'This link is no longer available');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    const imageUrl = data?.image_url || data?.image_link;
    if (imageUrl) {
      const link = document.createElement('a');
      link.href = imageUrl;
      link.download = `vto-${shareToken}.png`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl border border-slate-200 p-8 max-w-md w-full text-center"
        >
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-xl font-bold text-slate-900 mb-2">Link Not Available</h1>
          <p className="text-slate-600 mb-6">{error}</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
          >
            Visit Nanokart.ai <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </div>
    );
  }

  const imageUrl = data?.image_url || data?.image_link;

  return (
    <>
      <SEO {...SEO_CONFIGS.sharedVTO} />
      <div className="min-h-screen bg-slate-50" data-testid="shared-vto-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-blue-600 to-blue-500 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-slate-900">Nanokart.ai</span>
          </Link>
          <div className="text-sm text-slate-500">
            Powered by Virtual Try-On
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl border border-slate-200 overflow-hidden"
        >
          {/* Business Header */}
          <div className="p-6 border-b border-slate-100 flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Shared by</p>
              <h1 className="text-xl font-bold text-slate-900">{data?.business_name}</h1>
            </div>
            <button
              onClick={handleDownload}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Download
            </button>
          </div>

          {/* Image */}
          <div className="p-6 bg-slate-50">
            <div className="aspect-square max-w-xl mx-auto bg-white rounded-xl overflow-hidden shadow-lg">
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt="Virtual Try-On Result"
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-slate-400">
                  Image not available
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 bg-gradient-to-r from-blue-50 to-purple-50 text-center">
            <p className="text-slate-600 mb-4">
              Want to create virtual try-on experiences for your customers?
            </p>
            <Link
              to="/request-access"
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
            >
              Get Started with Nanokart.ai <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </motion.div>

        {/* Info */}
        <div className="mt-6 text-center text-sm text-slate-500">
          <p>Created on {data?.created_at ? new Date(data.created_at).toLocaleDateString() : '-'}</p>
        </div>
      </main>
    </div>
    </>
  );
};

export default SharedVTO;
